import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Translate } from 'react-redux-i18n'
import { Element } from '../../../../../../../common/html/element'
import { Icon } from '../../../../../../../common/html/icon'
import { SCREEN } from '../../../../../../../common/selectCurrencyUnit/constant'
import { useFormatCurrency } from '../../../../../../../common/selectCurrencyUnit/useFormatCurrency'
import { Tooltip } from '../../../../../../../common/tooltip'
import { formatVal, valToPercent } from '../../../../../../../helpers/value'
import { EMPTY_VALUE } from '../../../../../../../utils/common'
import { PERIOD_TYPE } from '../../../../../constant'
import { getHighlightScore } from '../../../../../store/slice'
import { selectRiskScaleInterpretation } from '../../../../store/slice'
import styles from './index.module.scss'

const BoxInfo = ({ timeFrequency }) => {
  const { formatCurrencyWithUnit } = useFormatCurrency(SCREEN.SCORE)
  const riskScaleInterpretation = useSelector(selectRiskScaleInterpretation)
  const locale = useSelector((state) => state.i18n.locale)

  const {
    riskScore,
    prevRiskScore,
    riskScoreColor,
    modelSegmentName,
    yearReportCal,
    totalTradeCreditLimit,
    isAdjusted,
    noteAdjust,
    yoYChange,
    qoQChange,
    moMChange,
  } = useSelector(getHighlightScore())

  const valueChange = useMemo(() => {
    switch (timeFrequency) {
      case PERIOD_TYPE.YEARLY:
        return yoYChange
      case PERIOD_TYPE.QUARTERLY:
        return qoQChange
      case PERIOD_TYPE.MONTHLY:
        return moMChange
      default:
        return null
    }
  }, [timeFrequency, yoYChange, qoQChange, moMChange])

  const riskGrade = useMemo(() => {
    if (riskScaleInterpretation.length && riskScore !== null) {
      return riskScaleInterpretation.find(
        (item) =>
          riskScore <= item.scoreRange_Max && riskScore >= item.scoreRange_Min,
      )
    }
    return {}
  }, [riskScaleInterpretation, riskScore])

  const prevRiskGrade = useMemo(() => {
    if (riskScaleInterpretation.length && prevRiskScore) {
      return riskScaleInterpretation.find(
        (item) =>
          prevRiskScore <= item.scoreRange_Max &&
          prevRiskScore >= item.scoreRange_Min,
      )
    }
  }, [riskScaleInterpretation, prevRiskScore])

  const renderTooltipPreviousScore = () => {
    const lst = [
      {
        label: 'corporateRiskScore.previousScore',
        value: prevRiskScore,
      },
      {
        label: 'corporateRiskScore.previousRiskGrade',
        value: prevRiskGrade?.scoreRangeName,
      },
      {
        label: 'corporateRiskScore.previousPD',
        value: valToPercent(prevRiskGrade?.pd, locale, false, true),
      },
      {
        label: 'corporateRiskScore.previousRiskCategory',
        value: prevRiskGrade?.riskGradeTypeCode,
      },
    ]
    return (
      <div>
        {lst.map((item) => (
          <div key={item.label} className="dFlex justifyContentBetween gap3">
            <Translate value={item.label} /> {item.value}
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className={styles.boxInfo} id="boxInfoId">
      <Element style={{ fontSize: 16 }} className={styles.title}>
        <Translate value="riskScaleAndInterpretation.totalScoreRank" />
      </Element>
      <div className={styles.scoreRow}>
        <Element
          className={styles.scoreValue}
          style={{ backgroundColor: riskScoreColor || '#cccccc', fontSize: 34 }}
        >
          {formatVal(riskScore, locale, false, 0)}
        </Element>
        <Element
          className={styles.scoreGrade}
          style={{ backgroundColor: riskScoreColor || '#cccccc', fontSize: 34 }}
        >
          {riskGrade?.riskGradeTypeCode || EMPTY_VALUE}
        </Element>
      </div>
      <Element className={styles.riskLevel} style={{ fontSize: 14 }}>
        {riskGrade?.scoreRangeName || EMPTY_VALUE}
        <br />
        <div className={styles.noteAdjust}>{isAdjusted && noteAdjust}</div>
      </Element>
      <div className={styles.infoRow}>
        <div className={styles.infoBox}>
          <div className={styles.infoLabel}>
            <Translate value="riskScaleAndInterpretation.previousScore" />
          </div>
          <Tooltip tooltipContent={renderTooltipPreviousScore()}>
            <div className={styles.infoValueGreen}>
              {prevRiskScore ? (
                <div
                  className={styles.boxColor}
                  style={{
                    background: riskScoreColor,
                  }}
                />
              ) : null}
              {formatVal(prevRiskScore, locale, true, 0)}
            </div>
          </Tooltip>
        </div>
        <div className={styles.infoBox}>
          <div className={styles.infoLabel}>
            <Translate value="riskScaleAndInterpretation.change" />
          </div>
          <div className={styles.infoValueRed}>
            {!riskScore && !prevRiskScore ? null : (
              <span className={styles.arrowDown}>
                {valueChange > 0 ? (
                  <Icon
                    style={{ color: '#3DA967' }}
                    className="icon-caret-top"
                  />
                ) : valueChange < 0 ? (
                  <Icon
                    style={{ color: '#E0505B' }}
                    className="icon-caret-bottom"
                  />
                ) : null}
              </span>
            )}
            {!valueChange
              ? Number(valueChange) === 0
                ? '0'
                : EMPTY_VALUE
              : Math.abs(valueChange)}
          </div>
        </div>
        <div className={styles.infoBox}>
          <div className={styles.infoLabel}>
            <Translate value="riskScaleAndInterpretation.pd" />
          </div>
          <div className={styles.infoValueGreen}>
            {riskGrade?.pd ? (
              <div
                className={styles.boxColor}
                style={{
                  background: riskScoreColor,
                }}
              />
            ) : null}
            <>
              {riskGrade?.pd
                ? valToPercent(riskGrade?.pd, locale, false, true)
                : EMPTY_VALUE}
            </>
          </div>
        </div>
      </div>
      <ul className={styles.infoList}>
        <li>
          <Translate value="riskScaleAndInterpretation.currentScoreBased" />
          <b>{yearReportCal || EMPTY_VALUE}</b>
        </li>
        <li>
          <Translate value="riskScaleAndInterpretation.modelSegment" />
          <b>{modelSegmentName || EMPTY_VALUE}</b>
        </li>
        <li>
          <Translate value="riskScaleAndInterpretation.tradeCreditLimitRecommendation" />
          &nbsp;
          <b>{formatCurrencyWithUnit({ value: totalTradeCreditLimit })}</b>
        </li>
      </ul>
    </div>
  )
}

export default BoxInfo
